// docs.ts — runtime helpers for the in-app docs surface.
//
// Markdown lives in /public/docs/ (copied at build time by
// scripts/copy-docs.mjs). At runtime we fetch the manifest + each doc's
// body from there. Vite serves /public/* unchanged, so the URLs are stable
// in dev and production.
//
// Caching:
//   - Manifest cached in-module after first fetch.
//   - Each doc body cached in sessionStorage (key: cc-os.docs.body.<slug>)
//     so reopening the docs surface stays fast even after a reload.
//   - The cached value is keyed by manifest.generatedAt so a redeploy that
//     ships new docs invalidates the cache transparently.

export type DocCategory = "getting-started" | "workflows" | "reference";

export interface DocManifestEntry {
  slug: string;
  path: string;
  route: string;
  title: string;
  excerpt: string;
  category: DocCategory;
  bytes: number;
}

export interface DocManifest {
  generatedAt: string;
  docs: DocManifestEntry[];
}

const SESSION_KEY_PREFIX = "cc-os.docs.body.";

let manifestPromise: Promise<DocManifest> | null = null;

export async function fetchManifest(): Promise<DocManifest> {
  if (!manifestPromise) {
    manifestPromise = fetch("/docs/manifest.json", { cache: "no-cache" })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to load /docs/manifest.json (${res.status} ${res.statusText})`,
          );
        }
        return (await res.json()) as DocManifest;
      })
      .catch((err) => {
        // Reset so the next caller gets a fresh attempt rather than the
        // poisoned rejection.
        manifestPromise = null;
        throw err;
      });
  }
  return manifestPromise;
}

function sessionKey(slug: string, generatedAt: string): string {
  return `${SESSION_KEY_PREFIX}${slug}.${generatedAt}`;
}

/**
 * Fetch a doc's markdown body. Uses sessionStorage as an L1 cache keyed by
 * the manifest's generatedAt timestamp.
 */
export async function fetchDocBody(
  entry: DocManifestEntry,
  generatedAt: string,
): Promise<string> {
  const key = sessionKey(entry.slug, generatedAt);
  if (typeof sessionStorage !== "undefined") {
    const cached = sessionStorage.getItem(key);
    if (cached !== null) return cached;
  }
  const res = await fetch(`/docs/${entry.path}`, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(
      `Failed to load /docs/${entry.path} (${res.status} ${res.statusText})`,
    );
  }
  const body = await res.text();
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(key, body);
    } catch {
      // sessionStorage can throw on quota — fine, we just won't cache this one.
    }
  }
  return body;
}

/**
 * Lookup a doc by slug.
 */
export function findBySlug(
  manifest: DocManifest,
  slug: string,
): DocManifestEntry | undefined {
  return manifest.docs.find((d) => d.slug === slug);
}

/**
 * Group manifest entries by category, preserving order.
 */
export function byCategory(
  manifest: DocManifest,
): Record<DocCategory, DocManifestEntry[]> {
  const out: Record<DocCategory, DocManifestEntry[]> = {
    "getting-started": [],
    workflows: [],
    reference: [],
  };
  for (const d of manifest.docs) {
    out[d.category].push(d);
  }
  return out;
}

/**
 * Slugify a heading string the same way rehype-slug does, so our TOC anchors
 * line up with the renderer's `<h2 id=...>` output.
 *
 * github-slugger's algorithm: lowercase, strip anything not [a-z0-9-_ ],
 * collapse whitespace to dashes. Dedupe of repeated slugs happens in
 * `parseToc` below — `slugifyHeading` itself stays pure so callers that
 * just need the base slug (e.g. anchor lookups) get a stable result.
 */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/**
 * Parse H2 headings out of a markdown document for the sticky TOC sidebar.
 * Skips fenced code blocks so a `## something` inside a ```code``` fence
 * doesn't show up.
 *
 * Dedupes repeated slugs the same way github-slugger does (used internally
 * by rehype-slug): the first occurrence keeps the bare slug, subsequent
 * collisions append `-1`, `-2`, ... so the rendered headings and the TOC
 * links agree on every id. Without this the TOC would render two `<li>`
 * with the same React key (e.g. `cmdk-command-palette`) on long docs that
 * reuse a heading title in different sections.
 */
export interface TocEntry {
  text: string;
  id: string;
  level: 2 | 3;
}

export function parseToc(markdown: string): TocEntry[] {
  const lines = markdown.split(/\r?\n/);
  const out: TocEntry[] = [];
  const seen = new Map<string, number>();
  let inCode = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("```") || line.startsWith("~~~")) {
      inCode = !inCode;
      continue;
    }
    if (inCode) continue;
    const m = line.match(/^(#{2,3})\s+(.+?)\s*$/);
    if (!m) continue;
    const level = m[1].length === 2 ? 2 : 3;
    const text = m[2].replace(/\s*\{#[^}]*\}\s*$/, "").trim();
    if (!text) continue;
    const base = slugifyHeading(text);
    const count = seen.get(base) ?? 0;
    const id = count === 0 ? base : `${base}-${count}`;
    seen.set(base, count + 1);
    out.push({ text, id, level });
  }
  return out;
}
